export const companyProfilePages = {
  onePage: {
    slug: "company-overview",
    clientNavLabel: "Executive overview",
    heroHeading: "Company overview",
    seoTitle: "OctalChip: AI Engineering Company Overview",
    title: "OctalChip Company Profile",
    subtitle: "AI engineering partner for generative AI, agents, RAG & LLMOps",
    intro:
      "OctalChip helps startups, SMEs, and enterprise teams ship production generative AI systems (agents, RAG, copilots, and AI-native products) with a clear focus on outcomes, quality, and speed.",
    fitFor: "Executive briefings and fast alignment with stakeholders.",
    stats: [
      { label: "Team strength", value: "25+" },
      { label: "Projects shipped", value: "8+" },
      { label: "Years delivering", value: "4+" },
      { label: "Client relationships", value: "55+" },
      { label: "Technologies in use", value: "35+" },
    ],
    industries: ["Fintech", "Healthcare", "Banking", "Manufacturing", "Real Estate", "E-commerce", "SaaS"],
    process: ["Discovery and planning", "Design and validation", "Agile AI delivery and QA", "Launch and scale", "Support and growth"],
    outcomes: [
      "Faster time to market for AI products and copilots",
      "Stronger platform reliability and production LLMOps stability",
      "Reduced manual effort through AI workflow automation",
      "Scalable foundations for multi-market AI growth",
    ],
    security: [
      "HTTPS and TLS-first implementation",
      "Role-based access controls and secure Cloud AI setup",
      "Privacy-first engineering with GDPR-aware delivery",
    ],
    engagementModels: ["Fixed scope", "Dedicated AI team", "Hourly"],
    sections: [
      {
        heading: "What we deliver",
        bullets: [
          "Generative AI apps, agents, RAG systems, and copilots",
          "AI-native web and AI-powered mobile products",
          "AI workflow automation with n8n, Zapier, Make, and LLM tooling",
          "Cloud AI & LLMOps on AWS, Azure, and GCP",
        ],
      },
      {
        heading: "Why teams choose OctalChip",
        bullets: [
          "End-to-end AI delivery from strategy to production",
          "Strong technical depth across generative AI, agents, RAG, and cloud AI",
          "Agile execution with transparent communication",
          "Security-focused and compliance-aware implementation",
        ],
      },
    ],
  },
  detailed: {
    slug: "capabilities-and-delivery",
    clientNavLabel: "Capabilities & delivery",
    heroHeading: "Capabilities & how we deliver",
    seoTitle: "OctalChip: AI Capabilities & Delivery",
    title: "OctalChip Detailed Company Profile",
    subtitle: "Depth for procurement, architecture review, and AI delivery planning",
    intro:
      "From discovery to post-launch support, we work as an extension of your team to deliver production-ready generative AI systems that scale.",
    fitFor: "Procurement, technical due diligence, and detailed partner vetting.",
    stats: [
      { label: "Team strength", value: "25+" },
      { label: "Projects shipped", value: "8+" },
      { label: "Years delivering", value: "4+" },
      { label: "Client relationships", value: "55+" },
      { label: "Technologies in use", value: "35+" },
    ],
    industries: ["Fintech", "Healthcare", "Banking", "Manufacturing", "Real Estate", "E-commerce", "SaaS"],
    process: ["Goal and KPI discovery", "AI solution blueprint and UX validation", "Sprint delivery with demos", "Go-live and observability", "Post-launch optimization"],
    outcomes: [
      "Clear delivery predictability with milestone tracking",
      "Improved AI product quality through evals and QA practice",
      "Faster iteration velocity after initial release",
      "Lower operational risk with Cloud AI & LLMOps discipline",
    ],
    security: [
      "Secure SDLC mindset across planning to release",
      "Infrastructure and deployment hardening best practices",
      "Compliance-aligned implementation standards",
    ],
    engagementModels: ["Project-based fixed scope", "Dedicated monthly AI team", "Hourly engagement"],
    sections: [
      {
        heading: "How we run engagements",
        bullets: [
          "Discovery and planning",
          "Design and validation",
          "Agile AI development with QA",
          "Launch, scale, and ongoing support",
        ],
      },
      {
        heading: "Scale & track record",
        bullets: [
          "25+ agile-enabled employees",
          "8+ delivered projects",
          "4+ years of experience",
          "55+ satisfied clients",
          "35+ technologies mastered",
        ],
      },
    ],
  },
  buyers: {
    slug: "website-and-digital-services",
    clientNavLabel: "AI products & growth programs",
    heroHeading: "AI-native products & growth",
    seoTitle: "OctalChip: AI-Native Products & Growth Services",
    title: "OctalChip Profile for Service Buyers",
    subtitle: "AI-native web, apps, and automation for leads, experience, and operations",
    intro:
      "We help founders and growth teams launch or upgrade AI-native websites, web apps, mobile experiences, and AI automation systems that improve lead flow and operations.",
    fitFor: "Founders and growth leaders choosing an AI engineering delivery partner.",
    stats: [
      { label: "Team strength", value: "25+" },
      { label: "Projects shipped", value: "8+" },
      { label: "Years delivering", value: "4+" },
      { label: "Client relationships", value: "55+" },
      { label: "Technologies in use", value: "35+" },
    ],
    industries: ["Fintech", "Healthcare", "Banking", "Manufacturing", "Real Estate", "E-commerce", "SaaS"],
    process: ["Business requirement discovery", "Wireframes and AI solution blueprint", "Sprint-based implementation", "QA and go-live support", "Maintenance and enhancements"],
    outcomes: [
      "Better lead quality and conversion journey performance",
      "Higher user engagement with AI-assisted UX flows",
      "Faster internal operations through AI workflow automation",
      "Stable growth architecture with Cloud AI-ready foundations",
    ],
    security: [
      "Secure implementation and access controls",
      "Privacy-aware data handling patterns",
      "Production-readiness with backup and reliability focus",
    ],
    engagementModels: ["Fixed scope", "Dedicated AI team", "Hourly"],
    sections: [
      {
        heading: "Where we plug in",
        bullets: [
          "AI-native business websites and company portals",
          "E-commerce and conversion-focused AI experiences",
          "Custom AI web applications for operations and workflows",
          "AI-powered mobile apps, Cloud AI setup, and LLM features",
        ],
      },
      {
        heading: "Outcomes you should expect",
        bullets: [
          "Better lead quality and customer journeys",
          "Higher engagement and retention",
          "Faster cycles through AI workflow automation",
          "Improved reliability through Cloud AI & LLMOps practices",
        ],
      },
    ],
  },
};

export const developerProfiles = [
  {
    slug: "yagnik-pokal",
    name: "Yagnik Pokal",
    headline: "Senior backend engineer, Go & TypeScript",
    summary:
      "If you are staffing backend capacity, Yagnik fits when you need services and integrations owned end to end with production discipline: clear APIs, solid architecture, and less rework as you scale.",
    highlights: [
      "Roughly ten years focused on backend and platform work",
      "Strong in Go and TypeScript for services and tooling",
      "Comfort owning design, reliability, and maintainability decisions",
    ],
    capabilities: [
      "Translates backlog and stakeholder input into APIs and microservices ready for QA and release",
      "Improves stability and performance without blocking your delivery schedule",
      "Keeps integrations, data flows, and service boundaries understandable for your full team",
    ],
    engagementFit: [
      "You need a backend lead or senior IC for greenfield platforms or bounded contexts",
      "You are refactoring or uplifting a legacy backend while still shipping milestones",
      "You depend on integrations, pipelines, or high-traffic paths that cannot fail quietly",
    ],
  },
  {
    slug: "aryan-amipara",
    name: "Aryan Amipara",
    headline: "Software developer, squad delivery & roadmap execution",
    summary:
      "If you hire for product-oriented execution, Aryan aligns well with backlog-driven teams: iterative releases, stakeholder feedback, and features that ship with context on user value.",
    highlights: [
      "Hands-on contributor across feature breakdown and sprint delivery",
      "Comfortable aligning with product, design, and engineering leads daily",
      "Used to pacing work so demos and releases stay predictable",
    ],
    capabilities: [
      "Builds and refines customer-facing flows and internal tooling from clarified acceptance criteria",
      "Participates proactively in refinement, demos, and post-release follow-up",
      "Reduces ambiguity between roadmap intent and shipped behavior",
    ],
    engagementFit: [
      "You need extra capacity inside an existing agile or squad model",
      "Your roadmap emphasizes rapid experiments, iterations, or feature expansion",
      "You want engineers who cooperate tightly with PM and design counterparts",
    ],
  },
  {
    slug: "fenil-kachhadiya",
    name: "Fenil Kachhadiya",
    headline: "Software developer, quality & dependable throughput",
    summary:
      "If you want quality alongside velocity in your hire, Fenil suits teams that care about tidy implementation and steady output: dependable sprint contributions without cutting corners reviewers would flag later.",
    highlights: [
      "Quality-first habits on implementations and collaborative review",
      "Reliable pacing across milestones and refinement cycles",
      "Team-oriented delivery that preserves shared standards over time",
    ],
    capabilities: [
      "Implements specs with attention to regressions, edge cases, and long-term readability",
      "Helps stabilize patterns and conventions the rest of the team can reuse",
      "Supports ongoing product growth with consistent craftsmanship",
    ],
    engagementFit: [
      "You need scalable feature capacity with disciplined code review and standards",
      "You are balancing speed with maintainability across multiple releases",
      "You expect long-lived products where onboarding time and rework must stay low",
    ],
  },
  {
    slug: "yagnik-pithva",
    name: "Yagnik Pithva",
    headline: "Full-stack engineer, .NET Core, Angular & React",
    summary:
      "If you need a full-stack engineer who can own APIs, cloud-backed workflows, and polished front ends together, Yagnik fits teams building accounting or fintech experiences: .NET Core, Angular and React, Azure Functions, and data on MS SQL Server and MongoDB with integrations such as QuickBooks and Xero.",
    highlights: [
      "Production work on .NET Core Web APIs, Entity Framework, Azure Functions, and REST integrations",
      "Strong Angular and React delivery with Material, PrimeNG, Tailwind, and state patterns using RxJS and NgRx",
      "Background in fintech Angular apps, MEAN-stack products, and freelance work with UX and performance outcomes",
    ],
    capabilities: [
      "Builds and extends accounting and operations software with third-party platform integrations and clear service boundaries",
      "Delivers responsive, accessible interfaces and modular serverless components that stay maintainable as scope grows",
      "Works comfortably across JavaScript, TypeScript, and C# with SQL and document databases in the same engagement",
    ],
    engagementFit: [
      "You are shipping or scaling .NET backends with Angular or React clients and need one engineer across the stack",
      "You need QuickBooks, Xero, or similar integrations with reliable persistence and operational workflows",
      "You want someone used to agile delivery in regulated or data-sensitive domains such as finance and health services",
    ],
  },
];
